'use client'

import { convertAddressStringToObject } from '@repo/utils/addresses'
import { CustomDataFieldAddress } from '@repo/custom-data-pages/CustomDataFieldAddress'
import { CustomDataSaveButtons } from '@repo/custom-data-pages/CustomDataSaveButtons'
import { DateSelection } from '@repo/ui/DateSelection'
import { ExpertiseAreaSelect } from '@repo/ui/ExpertiseAreaSelect'
import { ExpertTypeSelect } from '@repo/ui/ExpertTypeSelect'
import { MarketingIntroductionSourcesSelect } from '@repo/ui/MarketingIntroductionSourcesSelect'
import { MarketingMailingCategoriesSelect } from '@repo/ui/MarketingMailingCategoriesSelect'
import { OrganisationSelect } from '@repo/ui/OrganisationSelect'
import { TextEntry } from '@repo/ui/TextEntry'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useState, useEffect } from 'react'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const ContactsAddEditForm = ({ contactId, onSuccess, onCancel, testData }) => {
  const {
    contactsById,
    createContact,
    expertAreasById,
    expertTypesById,
    fetchContactById,
    organisationsById,
    updateContact
  } = useLookupsStore()

  const [formData, setFormData] = useState({
    title: null,
    forename: null,
    initials: null,
    surname: null,
    email: null,
    phoneNo: null,
    mobileNo: null,
    faxNo: null,
    house: null,
    area: null,
    postalTown: null,
    county: null,
    postcode: null,
    websiteUrl: null,
    notes: null,
    expertTypeId: null,
    expertiseAreaId: null,
    organisationId: null,
    marketingMailingCategoryId: null,
    marketingIntroductionSourceId: null,
    hasOptedInToEmail: false,
    optedInToEmailDateTime: new Date(),
    optedInToEmailSourceUrl: null
  })

  const [address, setAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [validationMessage, setValidationMessage] = useState(null)

  // Load existing contact data if editing
  useEffect(() => {
    if (contactId && contactsById[contactId]) {
      const contact = contactsById[contactId]
      setFormData({
        title: contact.title || null,
        forename: contact.forename || null,
        initials: contact.initials || null,
        surname: contact.surname || null,
        email: contact.email || null,
        phoneNo: contact.phoneNo || null,
        mobileNo: contact.mobileNo || null,
        faxNo: contact.faxNo || null,
        house: contact.house || null,
        area: contact.area || null,
        postalTown: contact.postalTown || null,
        county: contact.county || null,
        postcode: contact.postcode || null,
        websiteUrl: contact.websiteUrl || null,
        notes: contact.notes || null,
        expertTypeId: contact.expertTypeId || null,
        expertiseAreaId: contact.expertiseAreaId || null,
        organisationId: contact.organisationId || null,
        marketingMailingCategoryId: contact.marketingMailingCategoryId || null,
        marketingIntroductionSourceId: contact.marketingIntroductionSourceId || null,
        hasOptedInToEmail: contact.hasOptedInToEmail || false,
        optedInToEmailDateTime: contact.optedInToEmailDateTime ? new Date(contact.optedInToEmailDateTime) : new Date(),
        optedInToEmailSourceUrl: contact.optedInToEmailSourceUrl || null
      })

      const address = [contact.house, contact.area, contact.postalTown, contact.county, contact.postcode].join('|')
      setAddress(address)
    }
  }, [contactId, contactsById, expertTypesById])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddressChange = (value) => {
    const address = convertAddressStringToObject(value)

    setFormData((prev) => ({
      ...prev,
      ...address
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.surname?.trim()) {
      newErrors.surname = 'Surname is required'
    }

    if (formData?.email && (!formData?.email?.includes('@') || !formData?.email?.includes('.'))) {
      newErrors.email = 'Please enter a valid email'
    }

    setErrors(newErrors)
    if (Object.entries(newErrors).length) console.log(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setValidationMessage(null)
      return
    }

    setIsLoading(true)

    try {
      if (contactId) {
        // Update existing contact
        await updateContact({ contactId, ...formData })

        if (onSuccess) onSuccess({ contactId })
      } else {
        // Create new contact
        const newContact = await createContact(formData)
        await fetchContactById({ id: newContact.contactId })
        if (onSuccess) onSuccess({ contactId: newContact.contactId })
      }
    } catch (error) {
      console.error('Error saving contact:', error)
      setErrors({ submit: 'Failed to save contact. Please try again.' })

      const { message } = error?.response?.data || {}
      setValidationMessage(message || null)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} data-testid="ContactsAddEditForm">
      <MUIStack
        direction="column"
        gap={2}
        sx={{
          width: '100%',
          backgroundColor: '#DFD1B655',
          padding: '20px 20px 30px',
          borderRadius: '6px',
          position: 'relative'
        }}
      >
        <MUIStack direction="row" justifyContent="flex-end" gap={2}>
          <CustomDataSaveButtons
            onSave={handleSubmit}
            onCancel={onCancel}
            sx={{ width: '200px', position: contactId ? 'absolute' : 'static', right: 0, top: '-65px' }}
            disabled={isLoading}
            type="submit"
          />
        </MUIStack>

        <ValidationErrorAlert errorCount={Object.keys(errors).length} isTop validationMessage={validationMessage} />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2 }}>
          Name
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="title"
            label="Title"
            value={formData.title}
            onChange={(value) => handleInputChange('title', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />
          <TextEntry
            name="initials"
            label="Initials"
            value={formData.initials}
            onChange={(value) => handleInputChange('initials', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={10}
            errors={errors}
          />
        </MUIStack>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="forename"
            label="Forename"
            value={formData.forename}
            onChange={(value) => handleInputChange('forename', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />

          <TextEntry
            name="surname"
            label="Surname"
            value={formData.surname}
            onChange={(value) => handleInputChange('surname', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            required
            errors={errors}
          />
        </MUIStack>

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Type
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <ExpertTypeSelect
            value={expertTypesById[formData.expertTypeId] || null}
            sx={{ minWidth: '240px', flex: 1 }}
            testData={testData}
            onChange={(value) => {
              handleInputChange('expertTypeId', value?.expertTypeId || null)
            }}
            errors={errors}
          />

          <ExpertiseAreaSelect
            value={expertAreasById[formData.expertiseAreaId] || null}
            testData={testData}
            onChange={(value) => {
              handleInputChange('expertiseAreaId', value?.expertiseAreaId || null)
            }}
            sx={{ minWidth: '240px', flex: 1 }}
            errors={errors}
          />
        </MUIStack>

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Organisation
        </MUITypography>
        <MUIStack direction="row" gap={2}>
          <OrganisationSelect
            value={organisationsById[formData.organisationId] || null}
            testData={testData}
            onChange={(value) => {
              handleInputChange('organisationId', value?.organisationId || null)
            }}
            errors={errors}
          />
        </MUIStack>

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Contact details
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="email"
            label="Email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={100}
            type="email"
            errors={errors}
          />
          <TextEntry
            name="phoneNo"
            label="Phone"
            value={formData.phoneNo}
            onChange={(value) => handleInputChange('phoneNo', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />
        </MUIStack>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="mobileNo"
            label="Mobile"
            value={formData.mobileNo}
            onChange={(value) => handleInputChange('mobileNo', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />
          <TextEntry
            name="faxNo"
            label="Fax"
            value={formData.faxNo}
            onChange={(value) => handleInputChange('faxNo', value)}
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            size="small"
            limit={20}
            errors={errors}
          />
        </MUIStack>
        <TextEntry
          name="websiteUrl"
          label="URL"
          value={formData.websiteUrl}
          onChange={(value) => handleInputChange('websiteUrl', value)}
          size="small"
          sx={{ backgroundColor: 'white', minWidth: '240px' }}
          limit={100}
          errors={errors}
        />

        <CustomDataFieldAddress value={address} onChange={(value) => handleAddressChange(value)} sx={{ mt: 8 }} />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Notes
        </MUITypography>
        <TextEntry
          name="notes"
          label={null}
          value={formData.notes}
          onChange={(value) => handleInputChange('notes', value)}
          sx={{ backgroundColor: 'white', minWidth: '240px' }}
          size="small"
          multiline
          rows={12}
          errors={errors}
        />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Marketing Details
        </MUITypography>

        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <MarketingMailingCategoriesSelect
            value={formData.marketingMailingCategoryId}
            sx={{ minWidth: '240px', flex: 1 }}
            testData={testData}
            onChange={(value) => {
              handleInputChange('marketingMailingCategoryId', value?.marketingMailingCategoryId || null)
            }}
            errors={errors}
          />
          <MarketingIntroductionSourcesSelect
            value={formData.marketingIntroductionSourceId}
            sx={{ minWidth: '240px', flex: 1 }}
            testData={testData}
            onChange={(value) => {
              handleInputChange('marketingIntroductionSourceId', value?.marketingIntroductionSourceId || null)
            }}
            errors={errors}
          />
        </MUIStack>

        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={formData.hasOptedInToEmail}
              onChange={(e) => handleInputChange('hasOptedInToEmail', e.target.checked)}
            />
          }
          label="Opted in to email"
        />
        {formData.hasOptedInToEmail ? (
          <MUIStack direction="row" gap={2} flexWrap="wrap">
            <DateSelection
              label="Opted in to email date"
              value={formData.optedInToEmailDateTime}
              onChange={(value) => handleInputChange('optedInToEmailDateTime', value)}
              size="small"
              name="optedInToEmailDateTime"
              errors={errors}
              sx={{ flex: 1, minWidth: '240px' }}
            />
            <TextEntry
              name="optedInToEmailSourceUrl"
              label="Opted in to email source URL"
              value={formData.optedInToEmailSourceUrl}
              onChange={(value) => handleInputChange('optedInToEmailSourceUrl', value)}
              size="small"
              sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
              errors={errors}
            />
          </MUIStack>
        ) : null}

        <ValidationErrorAlert errorCount={Object.keys(errors).length} validationMessage={validationMessage} />

        <MUIStack direction="row" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
          <CustomDataSaveButtons
            onSave={handleSubmit}
            onCancel={onCancel}
            sx={{ width: '200px' }}
            disabled={isLoading}
            type="submit"
          />
        </MUIStack>
      </MUIStack>
    </form>
  )
}

ContactsAddEditForm.propTypes = {
  contactId: PropTypes.any,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  testData: PropTypes.array
}
